import React, { memo, useEffect, useRef, useState } from "react";
import { IconNormal, IconCircleFilledIcon } from "../ui/Icon";
import UtilityHelper from "../../../service/utilities/UtilityHelper";
import AppTheme from "../../../service/app_config/AppTheme";
import AppTranslation from "../../../service/app_translation/AppTranslation";
import passwordHint, { PasswordHintResonseType } from "../ui/PasswordHint";
import TimePicker from "react-time-picker";
import { useDispatch } from "react-redux";
import { addModal } from "../../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../../const/ModalKeys";
import { isMobile } from "react-device-detect";

type Props = {
  name: string;
  type:
    | "text"
    | "number"
    | "password"
    | "email"
    | "search-input"
    | "phone"
    | "datepicker"
    | "timepicker";
  className?: string;
  placeHolder?: string;
  label?: string;
  value?: string;
  validate?: boolean | false;
  handleOnChange?: (
    fieldName: string,
    fieldValue: string,
    isValid?: boolean | true
  ) => void;

  prefixType?: string | "text" | "icon"; // text, icon
  prefixText?: string;
  prefixIcon?: string;
  prefixIconFill?: boolean;
  prefixHandleClick?: () => void;

  suffixType?: string | "text" | "icon"; // text, icon
  suffixText?: string;
  suffixIcon?: string;
  suffixIconFill?: boolean;
  suffixHandleClick?: () => void;

  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;

  showPassHint?: boolean;
  autoFocus?: boolean;

  handleEnter?: () => void;
};

const Input: React.FC<Props> = ({
  type = "text",
  className = "",
  placeHolder = "",
  name,
  validate = false,
  handleOnChange = undefined,
  value,
  label = "",
  prefixType = "",
  prefixText = "",
  prefixIcon = "",
  prefixIconFill = false,
  prefixHandleClick = undefined,
  suffixType = "",
  suffixText = "",
  suffixIcon = "",
  suffixIconFill = false,
  suffixHandleClick = undefined,
  disabled = false,
  readOnly = false,

  error = false,
  showPassHint = false,

  autoFocus = false,

  handleEnter = undefined,
}) => {
  AppTheme.applyStyleConst();
  const inputRef = useRef<HTMLInputElement>(null);

  let fieldName: string = "",
    fieldValue: string = "";
  let classes: Array<string> = [];
  let prefixHtml: JSX.Element = <></>;
  let suffixHtml: JSX.Element = <></>;

  const phoneMaxLength = 9;
  const dispatch = useDispatch();

  const [inputValueTime, setInputValueTime] = useState<string>(value!);
  const [validateStatus, setValidateStatus] = useState<boolean>(true);
  const [showPasswordHint, setShowPasswordHint] = useState<boolean>(false);
  const [passwordHints, setPasswordHints] = useState<PasswordHintResonseType>({
    status: false,
    errorMsg: "",
    htmlList: <></>,
    generalHtml: <></>,
  });

  if (UtilityHelper.isNotEmpty(prefixType)) {
    classes.push("prefix-active");

    if (prefixType === "text" && prefixText !== "") {
      prefixHtml = (
        <div
          role={typeof prefixHandleClick === "function" ? "button" : ""}
          className="opacity-05 font-size-14"
          onClick={prefixHandleClick}
        >
          {AppTranslation.text(prefixText)}
        </div>
      );
    } else if (prefixType === "icon" && prefixIcon !== "") {
      prefixHtml = (
        <span
          role={typeof prefixHandleClick === "function" ? "button" : ""}
          onClick={prefixHandleClick}
        >
          {prefixIconFill ? (
            <IconCircleFilledIcon
              name={prefixIcon}
              size={24}
              color={
                type === "search-input" ? AppTheme.primary : AppTheme.label
              }
            />
          ) : (
            <IconNormal
              name={prefixIcon}
              size={24}
              color={
                type === "search-input" ? AppTheme.primary : AppTheme.label
              }
            />
          )}
        </span>
      );
    }
  }

  if (type === "phone") {
    classes.push("prefix-active");
  }

  if (UtilityHelper.isNotEmpty(suffixType)) {
    classes.push("suffix-active");

    if (suffixType === "text" && suffixText !== "") {
      suffixHtml = (
        <div
          role={typeof suffixHandleClick === "function" ? "button" : ""}
          className="opacity-05 font-size-14"
          onClick={suffixHandleClick}
        >
          {AppTranslation.text(suffixText)}
        </div>
      );
    } else if (suffixType === "icon" && suffixIcon !== "") {
      suffixHtml = (
        <span
          role={typeof suffixHandleClick === "function" ? "button" : ""}
          onClick={suffixHandleClick}
        >
          <div
            className={
              suffixIconFill
                ? "bgcolor-primary rounded-circle text-center d-center"
                : ""
            }
            style={{
              width: suffixIconFill ? 24 : "inherit",
              height: suffixIconFill ? 24 : "inherit",
              lineHeight: 1,
            }}
          >
            <IconNormal
              name={suffixIcon}
              size={suffixIconFill ? 24 - 10 : 24}
              color={
                suffixIconFill
                  ? AppTheme.white
                  : type === "search-input"
                  ? AppTheme.primary
                  : AppTheme.label
              }
            />
          </div>
        </span>
      );
    }
  }

  const handleOnChangePush = (event: React.ChangeEvent<HTMLInputElement>) => {
    fieldName = event.target.name.toLowerCase();
    fieldValue = event.target.value.toString();

    let errorCounter = 0;
    if (validate) {
      if (UtilityHelper.isEmpty(fieldValue)) {
        //} && type !== "phone") {
        errorCounter++;
      }
      if (
        UtilityHelper.isNotEmpty(fieldValue) &&
        type === "email" &&
        !UtilityHelper.isEmail(fieldValue)
      ) {
        errorCounter++;
      }
      if (type === "phone") {
        if (
          UtilityHelper.isNotEmpty(fieldValue) &&
          !UtilityHelper.phoneNumberValid(fieldValue)
          //fieldValue.length <= phoneMaxLength
        ) {
          errorCounter++;
        }

        if (
          UtilityHelper.isNotEmpty(prefixText) &&
          UtilityHelper.isEmpty(fieldValue)
        ) {
          errorCounter++;
        }
      }
    }

    setValidateStatus(errorCounter === 0);

    if (showPassHint) {
      setPasswordHints(passwordHint(fieldValue, true));
      if (passwordHint(fieldValue, true).status === false) {
        errorCounter++;
      }
    }

    if (type === "phone") {
      fieldValue = fieldValue.slice(0, phoneMaxLength);
    }

    handleOnChange?.(fieldName, fieldValue, errorCounter === 0);
  };

  const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    setValidateStatus(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      if (typeof handleEnter === "function") {
        handleEnter();
      }
    }
  };

  //const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {};

  const handleClickDatePicker = () => {
    //dispatch(datePickerSetDate(dateSelected));
    dispatch(
      addModal({ id: ModalKeys.datepicker, size: "md", hideHeader: true })
    );
  };

  const handleTimeChange = (newTime: string | null) => {
    console.log("Selected time:", newTime);
    setInputValueTime(newTime!);
    handleOnChange?.(name, newTime!, true);
  };

  useEffect(() => {
    //console.log("rendered input");
    if (autoFocus && !isMobile) {
      inputRef.current?.focus();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <div
        className={`form-group  ${classes.join(" ")}  ${
          !validateStatus ? "error" : ""
        } ${type === "phone" ? "prefix-phone" : ""} ${error ? "error" : ""}`}
        onClick={() => (type === "datepicker" ? handleClickDatePicker() : {})}
      >
        {UtilityHelper.isNotEmpty(prefixType) && (
          <div className={`prefix ${disabled ? "disabled" : ""}`}>
            {prefixHtml}
          </div>
        )}

        {type === "phone" && (
          <div
            role="button"
            className={`prefix color-theme-label pe-2   ${
              disabled ? "disabled" : ""
            }`}
            onClick={disabled ? () => {} : prefixHandleClick}
          >
            {UtilityHelper.isNotEmpty(prefixText) ? (
              <span>{prefixText}</span>
            ) : (
              <span className="color-black opacity-05">code</span>
            )}
            <IconNormal name="arrow_down" size={15} color={AppTheme.label} />
          </div>
        )}

        {type === "datepicker" ? (
          <input
            id={name}
            ref={inputRef}
            name={name}
            type="text"
            autoComplete="off"
            className={`form-control color-theme-label ${className}`}
            value={value}
            placeholder={AppTranslation.text(placeHolder)}
            //onClick={handleClickDatePicker}
            //onChange={(e) => handleOnChangePush(e)}
            readOnly={true}
          />
        ) : type === "timepicker" ? (
          <TimePicker
            onChange={handleTimeChange}
            value={inputValueTime}
            disableClock={true} // set true to hide analog clock
            clearIcon={null} // hide "x" clear icon
            format="hh:mm a" // optional 12-hour format
            className={`form-control color-theme-label ${className}`}
            hourPlaceholder="00"
            minutePlaceholder="00"
          />
        ) : (
          <input
            id={name}
            ref={inputRef}
            name={name}
            type={
              ["search-input"].includes(type)
                ? "text"
                : ["phone"].includes(type)
                ? "number"
                : type
            }
            autoComplete={type === "password" ? "new-password" : "off"}
            className={`form-control color-theme-label ${className}`}
            value={value}
            placeholder={AppTranslation.text(placeHolder)}
            onChange={(e) => {
              handleOnChangePush(e);
              setShowPasswordHint(true);
            }}
            onClick={(e) => {
              handleClick(e);
              setShowPasswordHint(true);
            }}
            onBlur={(e) => {
              handleOnChangePush(e);
              setShowPasswordHint(false);
            }}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            readOnly={readOnly}
          />
        )}

        {UtilityHelper.isNotEmpty(label) && placeHolder.trim() === "" && (
          <label className={`form-label color-theme-sublabel opacity-05`}>
            {AppTranslation.text(label)}
          </label>
        )}

        {UtilityHelper.isNotEmpty(suffixType) && (
          <div className="suffix color-theme-sublabel">{suffixHtml}</div>
        )}
      </div>

      {validate && showPasswordHint && showPassHint && (
        <div>{passwordHints.htmlList}</div>
      )}
    </>
  );
};

export default memo(Input);
